// src/pages/AirbnbManagementPage.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Modal, Form, Table, Badge } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext.js';
import { auth } from '../firebase.js';
import LoadingSpinner from '../components/common/LoadingSpinner.js';

const getToken = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');
  return await user.getIdToken();
};

export default function AirbnbManagementPage() {
  const { user } = useAuth();

  const [bookings, setBookings] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteExpireDays, setInviteExpireDays] = useState(7);
  const [generatedInvite, setGeneratedInvite] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

  // Load bookings and invites on mount
  useEffect(() => {
    if (user) {
      fetchBookings();
      fetchInvites();
    }
  }, [user]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/airbnb/admin/bookings`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();

      if (response.ok) {
        setBookings(data.bookings || []);
      } else {
        setError(data.error || 'Falha ao carregar bookings');
      }
    } catch (err) {
      setError('Erro ao carregar bookings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvites = async () => {
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/airbnb/invites`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();

      if (response.ok) {
        setInvites(data.invites || []);
      } else {
        console.error('Falha ao carregar convites:', data.error);
      }
    } catch (err) {
      console.error('Erro ao carregar convites:', err);
    }
  };

  const handleCreateInvite = async () => {
    if (!inviteEmail && !window.confirm('Criar convite sem email específico? Qualquer pessoa com o link poderá se registrar.')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/airbnb/invites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: inviteEmail || null,
          expiresInDays: inviteExpireDays
        })
      });

      const data = await response.json();

      if (response.ok) {
        setGeneratedInvite(data.invite);
        setSuccess('Convite criado com sucesso!');
        setInviteEmail('');
        fetchInvites();
      } else {
        setError(data.error || 'Falha ao criar convite');
      }
    } catch (err) {
      setError('Erro ao criar convite');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyInviteLink = (url) => {
    navigator.clipboard.writeText(url);
    setSuccess('Link copiado!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
    setGeneratedInvite(null);
    setInviteEmail('');
    setInviteExpireDays(7);
  };

  const isAuthorized = user?.isAdmin;
  if (!isAuthorized) {
    return (
      <Container className="py-4">
        <h5>Acesso negado</h5>
      </Container>
    );
  }

  if (loading && bookings.length === 0) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <Container className="py-4">
      <h2 className="mb-4">Gestão Airbnb</h2>

      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      <Row>
        {/* Bookings Section */}
        <Col lg={8} className="mb-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">📅 Todos os Bookings</h5>
            </Card.Header>
            <Card.Body>
              {bookings.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <p>Nenhum booking ainda</p>
                </div>
              ) : (
                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Cliente</th>
                      <th>Email</th>
                      <th>Telefone</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings
                      .sort((a, b) => new Date(a.booking_date) - new Date(b.booking_date))
                      .map((booking) => (
                        <tr key={booking.id}>
                          <td>
                            {new Date(booking.booking_date).toLocaleDateString('pt-BR', {
                              weekday: 'short',
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </td>
                          <td>{booking.client_name}</td>
                          <td>{booking.client_email}</td>
                          <td>{booking.client_phone}</td>
                          <td>
                            <Badge bg={booking.status === 'confirmed' ? 'success' : 'secondary'}>
                              {booking.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Invites Section */}
        <Col lg={4} className="mb-4">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">✉️ Convites</h5>
              <Button size="sm" variant="primary" onClick={() => setShowInviteModal(true)}>
                + Novo
              </Button>
            </Card.Header>
            <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {invites.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <p>Nenhum convite criado</p>
                </div>
              ) : (
                invites.map((invite) => (
                  <Card key={invite.id} className="mb-3" bg="light">
                    <Card.Body className="py-2 px-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        {invite.email ? (
                          <strong className="small">{invite.email}</strong>
                        ) : (
                          <em className="text-muted small">Convite aberto</em>
                        )}
                        <Badge
                          bg={
                            invite.status === 'used' ? 'success' :
                            invite.status === 'expired' ? 'danger' :
                            'warning'
                          }
                        >
                          {invite.status}
                        </Badge>
                      </div>

                      <div className="small text-muted mb-2">
                        Criado: {new Date(invite.createdAt).toLocaleDateString('pt-BR')}
                        {invite.expiresAt && (
                          <> • Expira: {new Date(invite.expiresAt).toLocaleDateString('pt-BR')}</>
                        )}
                      </div>

                      {invite.status === 'pending' && invite.inviteUrl && (
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleCopyInviteLink(invite.inviteUrl)}
                          className="w-100"
                        >
                          📋 Copiar Link
                        </Button>
                      )}

                      {invite.status === 'used' && invite.usedAt && (
                        <div className="small text-muted">
                          Usado em: {new Date(invite.usedAt).toLocaleDateString('pt-BR')}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create Invite Modal */}
      <Modal show={showInviteModal} onHide={handleCloseInviteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Criar Novo Convite</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {generatedInvite ? (
            <div>
              <Alert variant="success">
                <strong>Convite criado com sucesso!</strong>
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label>Link do Convite</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    value={generatedInvite.inviteUrl}
                    readOnly
                    size="sm"
                  />
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => handleCopyInviteLink(generatedInvite.inviteUrl)}
                  >
                    📋 Copiar
                  </Button>
                </div>
              </Form.Group>

              {generatedInvite.email && (
                <p className="text-muted small">
                  Este convite é para <strong>{generatedInvite.email}</strong>
                </p>
              )}

              <p className="text-muted small mt-2">
                Expira: {new Date(generatedInvite.expiresAt).toLocaleDateString('pt-BR')}
              </p>
            </div>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Email (Opcional)</Form.Label>
                <Form.Control
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Deixe vazio para convite aberto"
                />
                <Form.Text className="text-muted">
                  Se especificado, apenas este email poderá usar o convite
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Validade (Dias)</Form.Label>
                <Form.Control
                  type="number"
                  value={inviteExpireDays}
                  onChange={(e) => setInviteExpireDays(parseInt(e.target.value))}
                  min="1"
                  max="365"
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {generatedInvite ? (
            <Button variant="primary" onClick={handleCloseInviteModal}>
              Fechar
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={handleCloseInviteModal}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateInvite}
                disabled={loading}
              >
                {loading ? 'Criando...' : 'Criar Convite'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
